import * as XLSX from "xlsx-js-style";
import * as moment from "moment-timezone";
import { getCurrencyFormat } from "./index.ts";
import { findChartDaysWorked } from "../services/payroll.service.ts";
import {
  collectReciboDeductionLines,
  countDailyPaymentsWithAcceptedExtras,
  getReciboJornadasCompletasCuadroDisplay,
  groupDeductionsForRecibo,
  isReciboRestDayEnjoyedRow,
  isReciboRestDayWorkedRow,
  mergeAbsenceRowsForRecibo,
  buildAbsenceOtrosIngresoRows,
  parseDeductionAmount,
  sumExtraHoursFromDailyPayments,
  summarizeChartDays,
} from "./payrollUtils.ts";
import {
  calculateDailyPayment,
} from "../services/payroll/ui.ts";
import { calcularTiempoTrabajado } from "../interfaces/formik/IReporteRelojChecador.ts";

interface PartnerData {
  partnerId: number;
  name: string;
  position: string;
  branch: string;
  salary: string;
  dailySalary: string;
  payPerHour: string;
  totalIncentives: string;
  totalDeductions: string;
  uniformDeduction?: string;
  totalHollydays?: number;
  celebrityDaysWorked?: number;
  celebrityDaysPayTotal?: string;
  freeDaysWorked?: number;
  freeDaysEnjoyed?: number;
  NormalCalculationNomina?: boolean;
}

interface PaymentDetailData {
  dailyPayments: any[];
  paymentDetails: any;
  schedules?: any[];
  realCheckRecords?: any[];
  absenceRows?: any[];
}

// Función para calcular el total de días festivos trabajados desde dailyPayments
// Igual que getCelebrityDaysTotalFromBreakdown en PaymentDetail.tsx
const calculateCelebrityDaysTotalFromBreakdown = (
  dailyPayments: any[],
  dailySalary: number,
  hourlyPay: number,
  NormalCalculationNomina: boolean,
  fullDayHours: number
): number => {
  if (!dailyPayments || dailyPayments.length === 0) {
    return 0;
  }

  let total = 0;
  dailyPayments.forEach((dp: any) => {
    if (dp.isCelebrityDayWorked && dp.celebrityDay) {
      const isNewSystem = dp.celebrityDay?.isNewSystem || false;
      
      if (isNewSystem && dp.celebrityDay.amount) {
        // Para nuevos bonos, usar el monto directamente
        total += Number(dp.celebrityDay.amount) || 0;
      } else {
        // Para legacy, calcular basándose en bonusType
        let hoursInSchedule = dp.hoursInSchedule || 0;
        const totalHours = dp.hours || 0;
        
        // Si no hay hoursInSchedule, calcularlo desde el schedule
        if (hoursInSchedule === 0 && dp.schedule?.startTime && dp.schedule?.endTime && dp.checkIn && dp.checkOut) {
          const [start] = dp.schedule.startTime.split(" - ");
          const [, end] = dp.schedule.endTime.split(" - ");
          const entradaProgramada = start + ":00";
          const salidaProgramada = end + ":00";
          const entradaRegistrada = dp.checkIn;
          const salidaRegistrada = dp.checkOut;

          if (salidaProgramada === "00:00:00") {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                hoursInSchedule = calcularTiempoTrabajado(
                  entradaRegistrada,
                  "24:00:00"
                );
              } else if (entradaRegistrada < entradaProgramada) {
                hoursInSchedule = calcularTiempoTrabajado(
                  entradaProgramada,
                  "24:00:00"
                );
              }
            }
          } else {
            if (entradaRegistrada !== salidaRegistrada) {
              if (entradaRegistrada >= entradaProgramada) {
                hoursInSchedule = calcularTiempoTrabajado(
                  entradaRegistrada,
                  salidaProgramada
                );
              } else if (
                entradaRegistrada < entradaProgramada &&
                salidaRegistrada > entradaProgramada
              ) {
                hoursInSchedule = calcularTiempoTrabajado(
                  entradaProgramada,
                  salidaProgramada
                );
              }
            }
          }
        }

        // Calcular el pago diario base
        const scheduleForPayment = {
          assistanceCheck_checkTime: dp.checkIn,
          assistanceCheckout_checkTime: dp.checkOut,
          schedule_startTime: dp.schedule?.startTime || "",
          schedule_endTime: dp.schedule?.endTime || "",
          incidents: dp.incidents || [],
          isCelebrityDayWorked: false, // No incluir el multiplicador aquí
          celebrityDay: null,
        };

        const dailyPayment = calculateDailyPayment(
          scheduleForPayment,
          dailySalary,
          hourlyPay,
          NormalCalculationNomina,
          hoursInSchedule,
          fullDayHours
        );
        
        // Para legacy, calcular solo el bono adicional (multiplicador - 1)
        if (dp.celebrityDay.bonusType) {
          const multiplierMatch = dp.celebrityDay.bonusType.match(/Salario x(\d+\.?\d*)/);
          if (multiplierMatch) {
            const multiplier = parseFloat(multiplierMatch[1]);
            // Solo sumar el bono adicional, no el salario completo
            const bonusAmount = dailyPayment * (multiplier - 1);
            total += bonusAmount;
          } else {
            total += dailyPayment;
          }
        } else {
          total += dailyPayment;
        }
      }
    }
  });

  return total;
};

export const generateReciboSheet = async (
  partnerData: PartnerData,
  paymentDetail: PaymentDetailData,
  dates: { start: any; end: any },
  branch: number,
  fullDayHours: number = 8.5
): Promise<any> => {
  const { dailyPayments, paymentDetails } = paymentDetail;
  const payment = paymentDetails;

  let startDateDisplay = "N/A";
  let endDateDisplay = "N/A";
  let paymentDate = "N/A";
  if (dates?.start && dates?.end) {
    const startDate = dates.start.clone().add(3, "days");
    const endDate = dates.end.clone().add(3, "days");
    startDateDisplay = startDate.format("DD-MM-YYYY");
    endDateDisplay = endDate.format("DD-MM-YYYY");

    const startDay = startDate.date();
    const endDay = endDate.date();

    if (startDay <= 15 && endDay <= 15) {
      paymentDate = "15";
    } else {
      const lastDayOfMonth = endDate.clone().endOf("month").date();
      paymentDate = lastDayOfMonth.toString();
    }
  }

  const reciboData: any[] = [];

  reciboData.push(["RECIBO DE NÓMINA", "", "", "", ""]);
  reciboData.push([]);

  reciboData.push(["NOMBRE DEL EMPLEADO:", partnerData.name || "", "", "", ""]);
  reciboData.push(["SUELDO BASE:", partnerData.salary || "", "", "", ""]);
  reciboData.push(["PUESTO:", partnerData.position || "", "", "", ""]);
  reciboData.push(["SUCURSAL:", partnerData.branch || "", "", "", ""]);
  reciboData.push([
    "PERIODO DE PAGO:",
    `${startDateDisplay} al ${endDateDisplay}`,
    "",
    "",
    "",
  ]);
  reciboData.push(["FECHA DE PAGO:", paymentDate, "", "", ""]);
  reciboData.push([]);

  const ingresosData: (string | number)[][] = [];

  let chartSummary = {
    totalDays: 0,
    completeDays: 0,
    hourlyDays: 0,
    extraDays: 0,
    doubleShiftDays: 0,
  };
  let chartDetailMap = new Map<string, any>();
  let chartDaysData: any = { detailed: [] };

  try {
    const chartResponse = await findChartDaysWorked(
      partnerData.partnerId,
      branch,
      dates.start.format("YYYY-MM-DD"),
      dates.end.format("YYYY-MM-DD")
    );
    chartDaysData = {
      detailed: chartResponse.detailed || [],
      graph: chartResponse.graph || [],
    };
    chartSummary = summarizeChartDays(chartDaysData.detailed || [], fullDayHours);
    chartDaysData.detailed.forEach((item: any) => {
      if (item?.fecha) {
        chartDetailMap.set(item.fecha, item);
      }
    });
  } catch (error) {
    console.error("Error fetching chart data:", error);
  }

  const getChartEntryForDailyPayment = (day: any) => {
    const formattedDate = moment(day.date, ["YYYY-MM-DD", "DD/MM/YYYY"]).format(
      "DD-MM-YYYY"
    );
    return chartDetailMap.get(formattedDate);
  };

  const chartCompleteDates = new Set(
    (chartDaysData?.detailed || [])
      .filter((item: any) => {
        const hours = Number(item?.horasRegistradas ?? 0);
        return hours >= fullDayHours && hours < fullDayHours * 2;
      })
      .map((item: any) => item.fecha)
  );

  const chartHourlyDates = new Set(
    (chartDaysData?.detailed || [])
      .filter((item: any) => {
        const hours = Number(item?.horasRegistradas ?? 0);
        return hours > 0 && hours < fullDayHours;
      })
      .map((item: any) => item.fecha)
  );

  // Procesar dailyPayments exactamente como en PaymentDetail.tsx
  const parseCurrencyValue = (value: any): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      return Number(value.replace(/[^0-9.-]+/g, "") || 0) || 0;
    }
    return 0;
  };

  const dailySalaryNumber = parseCurrencyValue(partnerData.dailySalary);
  const hourlyPayNumber = parseCurrencyValue(partnerData.payPerHour);

  let jornadasCompletasPuras = 0;
  let importeJornadasCompletas = 0;

  let pagosPorHoraCount = 0;
  let horasPagosPorHora = 0;
  let importePagosPorHora = 0;

  let turnoDobleCount = 0;
  let importeTurnoDoble = 0;

  dailyPayments.forEach((day: any) => {
    const concept = (day.concept || "").toLowerCase();
    const hours = parseFloat(day.hours || "0");

    // Descanso trabajado / disfrutado: no contar como jornada completa aunque hours >= fullDayHours
    if (
      isReciboRestDayWorkedRow(day) ||
      isReciboRestDayEnjoyedRow(day)
    ) {
      return;
    }

    // Excluir días festivos trabajados de otras categorías
    if (
      concept.includes("festivo trabajado") ||
      concept.includes("día festivo")
    ) {
      return;
    }

    // Turno doble
    if (concept.includes("doble jornada") || hours >= 16.5) {
      turnoDobleCount++;
      const paymentValue =
        typeof day.payment === "number"
          ? day.payment
          : parseCurrencyValue(day.payment);
      importeTurnoDoble += paymentValue;
      return;
    }

    // Pagos por hora
    if (
      concept.includes("pago por hora") ||
      (hours > 0 && hours < fullDayHours && !concept.includes("jornada completa"))
    ) {
      pagosPorHoraCount++;
      let conceptHours = hours;
      if (concept.includes("× pago por hora")) {
        const match = concept.match(
          /(\d+(?:\.\d+)?)\s*h\s*×\s*pago por hora/i
        );
        if (match) {
          conceptHours = parseFloat(match[1]);
        }
      }
      horasPagosPorHora += conceptHours;
      return;
    }

    if (concept.includes("jornada completa") && concept.includes("extras")) {
      jornadasCompletasPuras++;
      importeJornadasCompletas += dailySalaryNumber;
      return;
    }

    // Jornadas completas
    if (concept.includes("jornada completa")) {
      jornadasCompletasPuras++;
      importeJornadasCompletas += dailySalaryNumber;
      return;
    }

    // Si tiene horas >= fullDayHours, es jornada completa
    if (hours >= fullDayHours) {
      jornadasCompletasPuras++;
      importeJornadasCompletas += dailySalaryNumber;
    }
  });

  importePagosPorHora = horasPagosPorHora * hourlyPayNumber;

  const horasExtrasCount = sumExtraHoursFromDailyPayments(
    dailyPayments,
    fullDayHours
  );
  const diasConExtras = countDailyPaymentsWithAcceptedExtras(
    dailyPayments,
    fullDayHours
  );
  const importeHorasExtras = horasExtrasCount * hourlyPayNumber;

  const jornadasCuadro = getReciboJornadasCompletasCuadroDisplay(
    dailyPayments,
    fullDayHours
  );

  ingresosData.push([
    "Jornadas completas",
    jornadasCuadro.dias > 0 ? jornadasCuadro.dias : "",
    jornadasCuadro.dias > 0
      ? jornadasCuadro.horasNominales.toFixed(1)
      : "",
    importeJornadasCompletas > 0
      ? getCurrencyFormat(importeJornadasCompletas)
      : "",
  ]);

  ingresosData.push([
    "Pagos por hora",
    pagosPorHoraCount > 0 ? pagosPorHoraCount : "",
    horasPagosPorHora > 0 ? horasPagosPorHora.toFixed(1) : "",
    importePagosPorHora > 0 ? getCurrencyFormat(importePagosPorHora) : "",
  ]);

  ingresosData.push([
    "Horas extras",
    diasConExtras > 0 ? diasConExtras : "",
    horasExtrasCount > 0 ? horasExtrasCount.toFixed(1) : "",
    importeHorasExtras > 0 ? getCurrencyFormat(importeHorasExtras) : "",
  ]);

  ingresosData.push([
    "Turno doble",
    turnoDobleCount > 0 ? turnoDobleCount : "",
    turnoDobleCount > 0 ? (turnoDobleCount * fullDayHours * 2).toFixed(1) : "",
    importeTurnoDoble > 0 ? getCurrencyFormat(importeTurnoDoble) : "",
  ]);

  const freeDaysWorkedFromRows = dailyPayments.filter((day: any) =>
    isReciboRestDayWorkedRow(day)
  ).length;
  const freeDaysWorked = Math.max(
    payment.freeDaysWorked || 0,
    freeDaysWorkedFromRows
  );
  const freeDayWorkedPayTotalNumber = dailyPayments
    .filter((day: any) => isReciboRestDayWorkedRow(day))
    .reduce((sum: number, day: any) => {
      const paymentValue =
        typeof day.payment === "number"
          ? day.payment
          : parseCurrencyValue(day.payment);
      return sum + paymentValue;
    }, 0);
  const horasDiasDescansoTrabajados =
    freeDaysWorked > 0 ? freeDaysWorked * fullDayHours : 0;
  ingresosData.push([
    "Días de descanso trabajados",
    freeDaysWorked > 0 ? freeDaysWorked : "",
    horasDiasDescansoTrabajados > 0
      ? horasDiasDescansoTrabajados.toFixed(1)
      : "",
    freeDaysWorked > 0 ? getCurrencyFormat(freeDayWorkedPayTotalNumber) : "",
  ]);

  const freeDaysEnjoyedFromRows = dailyPayments.filter((day: any) =>
    isReciboRestDayEnjoyedRow(day)
  ).length;
  const freeDaysEnjoyed = Math.max(
    payment.freeDaysEnjoyed || 0,
    freeDaysEnjoyedFromRows
  );

  // Usar dailySalaryNumber ya declarado arriba
  const freeDaysEnjoyedPayNumber = freeDaysEnjoyed * dailySalaryNumber;
  const horasDiasDescansoDisfrutados =
    freeDaysEnjoyed > 0 ? freeDaysEnjoyed * fullDayHours : 0;
  ingresosData.push([
    "Días de descanso disfrutados",
    freeDaysEnjoyed > 0 ? freeDaysEnjoyed : "",
    horasDiasDescansoDisfrutados > 0
      ? horasDiasDescansoDisfrutados.toFixed(1)
      : "",
    freeDaysEnjoyed > 0 && freeDaysEnjoyedPayNumber > 0
      ? getCurrencyFormat(freeDaysEnjoyedPayNumber)
      : "",
  ]);

  const totalIncentivesNumber = parseCurrencyValue(payment.totalIncentives);
  const hasBonos = totalIncentivesNumber > 0;
  ingresosData.push([
    "Bonos",
    "",
    "",
    hasBonos ? getCurrencyFormat(totalIncentivesNumber) : "",
  ]);

  const celebrityDaysWorked = partnerData.celebrityDaysWorked || 0;
  // Calcular el total de días festivos trabajados desde schedules (igual que en PaymentDetail.tsx)
  // Usar schedules del backend si están disponibles, de lo contrario usar dailyPayments
  const NormalCalculationNomina = partnerData.NormalCalculationNomina || false;
  const effectiveSchedules = paymentDetail.schedules || [];
  let celebrityDaysPayTotalCalculated = 0;
  
  if (effectiveSchedules && effectiveSchedules.length > 0) {
    effectiveSchedules.forEach((schedule: any) => {
      if (schedule.isCelebrityDayWorked && schedule.celebrityDay) {
        const isNewSystem = schedule.celebrityDay.isNewSystem || false;
        
        if (isNewSystem && schedule.celebrityDay.amount) {
          // Para nuevos bonos, usar el monto directamente
          celebrityDaysPayTotalCalculated += Number(schedule.celebrityDay.amount) || 0;
        } else {
          // Para legacy, calcular basándose en bonusType
          let hoursInScheduleForSchedule = 0;
          if (
            schedule.assistanceCheck_checkTime &&
            schedule.assistanceCheckout_checkTime &&
            schedule.schedule_startTime &&
            schedule.schedule_endTime
          ) {
            const [start] = schedule.schedule_startTime.split(" - ");
            const [, end] = schedule.schedule_endTime.split(" - ");
            const entradaProgramada = start + ":00";
            const salidaProgramada = end + ":00";
            const entradaRegistrada = schedule.assistanceCheck_checkTime;
            const salidaRegistrada = schedule.assistanceCheckout_checkTime;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    "24:00:00"
                  );
                } else if (entradaRegistrada < entradaProgramada) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    "24:00:00"
                  );
                }
              }
            } else {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    salidaProgramada
                  );
                } else if (
                  entradaRegistrada < entradaProgramada &&
                  salidaRegistrada > entradaProgramada
                ) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    salidaProgramada
                  );
                }
              }
            }
          }

          const dailyPayment = calculateDailyPayment(
            schedule,
            dailySalaryNumber,
            hourlyPayNumber,
            NormalCalculationNomina,
            hoursInScheduleForSchedule,
            fullDayHours
          );
          
          // Para legacy, calcular solo el bono adicional (multiplicador - 1)
          if (schedule.celebrityDay.bonusType) {
            const multiplierMatch = schedule.celebrityDay.bonusType.match(/Salario x(\d+\.?\d*)/);
            if (multiplierMatch) {
              const multiplier = parseFloat(multiplierMatch[1]);
              // Solo sumar el bono adicional, no el salario completo
              const bonusAmount = dailyPayment * (multiplier - 1);
              celebrityDaysPayTotalCalculated += bonusAmount;
            } else {
              celebrityDaysPayTotalCalculated += dailyPayment;
            }
          } else {
            celebrityDaysPayTotalCalculated += dailyPayment;
          }
        }
      }
    });
  }
  
  // Fallback al cálculo desde dailyPayments si no hay schedules
  if (celebrityDaysPayTotalCalculated === 0) {
    celebrityDaysPayTotalCalculated = calculateCelebrityDaysTotalFromBreakdown(
      dailyPayments,
      dailySalaryNumber,
      hourlyPayNumber,
      NormalCalculationNomina,
      fullDayHours
    );
  }
  
  const celebrityDaysPayTotal =
    celebrityDaysPayTotalCalculated ||
    parseCurrencyValue(partnerData.celebrityDaysPayTotal || "0") ||
    0;
  const horasDiasFestivosTrabajados =
    celebrityDaysWorked > 0 ? celebrityDaysWorked * fullDayHours : 0;

  ingresosData.push([
    "Días festivos trabajados",
    celebrityDaysWorked > 0 ? celebrityDaysWorked : "",
    horasDiasFestivosTrabajados > 0
      ? horasDiasFestivosTrabajados.toFixed(1)
      : "",
    celebrityDaysWorked > 0 ? getCurrencyFormat(celebrityDaysPayTotal) : "",
  ]);

  const horasDiasFestivos =
    (partnerData.totalHollydays || 0) > 0
      ? (partnerData.totalHollydays || 0) * fullDayHours
      : 0;
  ingresosData.push([
    "Días festivos",
    (partnerData.totalHollydays || 0) > 0
      ? partnerData.totalHollydays || 0
      : "",
    horasDiasFestivos > 0 ? horasDiasFestivos.toFixed(1) : "",
    (partnerData.totalHollydays || 0) > 0
      ? getCurrencyFormat(
          (partnerData.totalHollydays || 0) *
            Number(partnerData.dailySalary?.replace(/[^0-9.-]+/g, "") || 0)
        )
      : "",
  ]);

  ingresosData.push(["Incapacidades", "", "", ""]);
  ingresosData.push(["Aguinaldo", "", "", ""]);
  ingresosData.push(["Vacaciones", "", "", ""]);
  ingresosData.push(["Prima vacacional", "", "", ""]);
  ingresosData.push(["Otros", "", "", ""]);

  const mergedAbsenceDaily = mergeAbsenceRowsForRecibo(
    dailyPayments,
    (paymentDetail as any).absenceRows
  );
  const { rows: absenceOtrosRows, totalAmount: absenceOtrosTotal } =
    buildAbsenceOtrosIngresoRows(
      mergedAbsenceDaily,
      fullDayHours,
      getCurrencyFormat
    );
  absenceOtrosRows.forEach((row) => ingresosData.push(row));

  const totalDeducciones = parseCurrencyValue(payment.totalDeductions);

  const rawDeductionLines = collectReciboDeductionLines({
    deductionBreakdown: payment.deductionBreakdown,
    calculatedNewDeductions: (paymentDetail as any).calculatedNewDeductions,
    calculatedMissingBoxes: (paymentDetail as any).calculatedMissingBoxes,
  });
  if (
    partnerData.uniformDeduction &&
    parseCurrencyValue(partnerData.uniformDeduction) > 0
  ) {
    rawDeductionLines.push({
      name: "Uniforme",
      amount: parseCurrencyValue(partnerData.uniformDeduction),
    });
  }

  const groupedDeductions = groupDeductionsForRecibo(rawDeductionLines);
  let sumDetailed = groupedDeductions.reduce((s, r) => s + r.amount, 0);
  sumDetailed = Math.round(sumDetailed * 100) / 100;
  const totalRounded = Math.round(totalDeducciones * 100) / 100;

  const deduccionesData: string[][] = groupedDeductions.map((row) => [
    row.displayName,
    getCurrencyFormat(row.amount),
  ]);

  if (totalRounded > sumDetailed + 0.01) {
    deduccionesData.push([
      "Otros",
      getCurrencyFormat(totalRounded - sumDetailed),
    ]);
  } else if (groupedDeductions.length === 0 && totalRounded > 0) {
    deduccionesData.push(["Otros", getCurrencyFormat(totalRounded)]);
  }

  // Calcular total de ingresos exactamente como en PaymentDetail.tsx
  const hollydaysTotal =
    (partnerData.totalHollydays || 0) > 0
      ? (partnerData.totalHollydays || 0) *
        parseCurrencyValue(partnerData.dailySalary)
      : 0;

  const totalIngresos =
    (importeJornadasCompletas || 0) +
    (importePagosPorHora || 0) +
    (importeHorasExtras || 0) +
    (importeTurnoDoble || 0) +
    freeDayWorkedPayTotalNumber +
    freeDaysEnjoyedPayNumber +
    totalIncentivesNumber +
    celebrityDaysPayTotal +
    hollydaysTotal +
    absenceOtrosTotal;

  // Calcular desde dailyPayments igual que en PaymentDetail.tsx:
  // totalDailyPayments ya incluye descanso disfrutado cuando aplica.
  const hoursAuthorizedPayNumber = dailyPayments.reduce(
    (sum: number, day: any) => {
      const paymentValue =
        typeof day.payment === "number"
          ? day.payment
          : parseCurrencyValue(day.payment);
      return sum + paymentValue;
    },
    0
  );
  // Usar hollydaysTotal ya declarado arriba
  const totalAPagar =
    hoursAuthorizedPayNumber +
    totalIncentivesNumber +
    hollydaysTotal -
    totalDeducciones;
  const netoRecibido = totalAPagar;

  const maxRows = Math.max(ingresosData.length, deduccionesData.length);

  reciboData.push(["INGRESOS", "", "", "", "DEDUCCIONES", ""]);
  reciboData.push([
    "CONCEPTO",
    "DÍAS",
    "HORAS",
    "IMPORTE",
    "CONCEPTO",
    "IMPORTE",
  ]);

  for (let i = 0; i < maxRows; i++) {
    const ingreso = ingresosData[i] || ["", "", "", ""];
    const deduccion = deduccionesData[i] || ["", ""];

    reciboData.push([
      ingreso[0],
      ingreso[1],
      ingreso[2],
      ingreso[3],
      deduccion[0],
      deduccion[1],
    ]);
  }

  reciboData.push([
    "INGRESOS TOTALES",
    "",
    "",
    getCurrencyFormat(totalIngresos),
    "DEDUCCIONES TOTALES",
    getCurrencyFormat(totalDeducciones),
  ]);
  reciboData.push([]);

  reciboData.push([
    `PAGO NETO RECIBIDO: ${getCurrencyFormat(netoRecibido)}`,
    "",
    "",
    "",
    "",
  ]);
  reciboData.push([]);

  reciboData.push([
    "Recibí la cantidad anotada en este recibo en pago de mi sueldo.",
  ]);
  reciboData.push([
    "Además certifico que no se me adeuda a la fecha, cantidad alguna por ningún concepto.",
  ]);
  reciboData.push([]);
  reciboData.push(["NOMBRE COMPLETO Y FIRMA:", ""]);
  reciboData.push(["_________________________", ""]);

  const worksheet = XLSX.utils.aoa_to_sheet(reciboData);

  const columnWidths = [
    { wch: 25 },
    { wch: 8 },
    { wch: 10 },
    { wch: 15 },
    { wch: 25 },
    { wch: 15 },
  ];
  worksheet["!cols"] = columnWidths;

  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

  worksheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 2, c: 1 }, e: { r: 2, c: 5 } },
    { s: { r: 3, c: 1 }, e: { r: 3, c: 5 } },
    { s: { r: 4, c: 1 }, e: { r: 4, c: 5 } },
    { s: { r: 5, c: 1 }, e: { r: 5, c: 5 } },
    { s: { r: 6, c: 1 }, e: { r: 6, c: 5 } },
    { s: { r: 7, c: 1 }, e: { r: 7, c: 5 } },
  ];

  if (worksheet["A1"]) {
    worksheet["A1"].s = {
      font: { bold: true, size: 20, color: { rgb: "FFFFFF" } },
      alignment: { horizontal: "center", vertical: "center" },
      fill: { fgColor: { rgb: "1F4E79" } },
      border: {
        top: { style: "thick", color: { rgb: "FFFFFF" } },
        bottom: { style: "thick", color: { rgb: "FFFFFF" } },
        left: { style: "thick", color: { rgb: "FFFFFF" } },
        right: { style: "thick", color: { rgb: "FFFFFF" } },
      },
    };
  }

  for (let row = 3; row <= 8; row++) {
    if (worksheet[`A${row}`]) {
      worksheet[`A${row}`].s = {
        font: { bold: true, size: 12, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2F5597" } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "FFFFFF" } },
          bottom: { style: "thin", color: { rgb: "FFFFFF" } },
          left: { style: "thin", color: { rgb: "FFFFFF" } },
          right: { style: "thin", color: { rgb: "FFFFFF" } },
        },
      };
    }

    if (worksheet[`B${row}`]) {
      worksheet[`B${row}`].s = {
        font: { size: 12, color: { rgb: "333333" } },
        fill: { fgColor: { rgb: "F8F9FA" } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "FFFFFF" } },
          bottom: { style: "thin", color: { rgb: "FFFFFF" } },
          left: { style: "thin", color: { rgb: "FFFFFF" } },
          right: { style: "thin", color: { rgb: "FFFFFF" } },
        },
      };
    }
  }

  let tablaStartRow = 0;
  for (let row = 1; row <= range.e.r; row++) {
    const cell = worksheet[`A${row}`];
    if (cell && String(cell.v || "") === "INGRESOS") {
      tablaStartRow = row;
      break;
    }
  }

  if (tablaStartRow > 0) {
    worksheet["!merges"].push(
      {
        s: { r: tablaStartRow - 1, c: 0 },
        e: { r: tablaStartRow - 1, c: 3 },
      },
      { s: { r: tablaStartRow - 1, c: 4 }, e: { r: tablaStartRow - 1, c: 5 } }
    );

    ["A", "E"].forEach((col) => {
      if (worksheet[`${col}${tablaStartRow}`]) {
        worksheet[`${col}${tablaStartRow}`].s = {
          font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: "2F5597" } },
          border: {
            top: { style: "thick", color: { rgb: "FFFFFF" } },
            bottom: { style: "thick", color: { rgb: "FFFFFF" } },
            left: { style: "thick", color: { rgb: "FFFFFF" } },
            right: { style: "thick", color: { rgb: "FFFFFF" } },
          },
        };
      }
    });

    const headerRow = tablaStartRow + 1;
    ["A", "B", "C", "D", "E", "F"].forEach((col) => {
      if (worksheet[`${col}${headerRow}`]) {
        worksheet[`${col}${headerRow}`].s = {
          font: { bold: true, size: 13, color: { rgb: "FFFFFF" } },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: "1F4E79" } },
          border: {
            top: { style: "thick", color: { rgb: "FFFFFF" } },
            bottom: { style: "thick", color: { rgb: "FFFFFF" } },
            left: { style: "thin", color: { rgb: "FFFFFF" } },
            right: { style: "thin", color: { rgb: "FFFFFF" } },
          },
        };
      }
    });

    for (let row = headerRow + 1; row <= range.e.r; row++) {
      const isTotalRow =
        worksheet[`A${row}`] &&
        (worksheet[`A${row}`].v === "INGRESOS TOTALES" ||
          worksheet[`A${row}`].v === "DEDUCCIONES TOTALES");

      const isNetoRow =
        worksheet[`A${row}`] &&
        worksheet[`A${row}`].v &&
        String(worksheet[`A${row}`].v).includes("PAGO NETO RECIBIDO");

      if (isNetoRow) {
        worksheet["!merges"].push({
          s: { r: row - 1, c: 0 },
          e: { r: row - 1, c: 5 },
        });
      }

      ["A", "B", "C", "D", "E", "F"].forEach((col) => {
        if (worksheet[`${col}${row}`]) {
          if (isTotalRow) {
            worksheet[`${col}${row}`].s = {
              font: { bold: true, size: 12, color: { rgb: "FFFFFF" } },
              alignment: { horizontal: "center", vertical: "center" },
              fill: { fgColor: { rgb: "1F4E79" } },
              border: {
                top: { style: "thick", color: { rgb: "FFFFFF" } },
                bottom: { style: "thick", color: { rgb: "FFFFFF" } },
                left: { style: "thin", color: { rgb: "FFFFFF" } },
                right: { style: "thin", color: { rgb: "FFFFFF" } },
              },
            };
          } else if (isNetoRow) {
            worksheet[`${col}${row}`].s = {
              font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
              alignment: { horizontal: "center", vertical: "center" },
              fill: { fgColor: { rgb: "2F5597" } },
              border: {
                top: { style: "thick", color: { rgb: "FFFFFF" } },
                bottom: { style: "thick", color: { rgb: "FFFFFF" } },
                left: { style: "thick", color: { rgb: "FFFFFF" } },
                right: { style: "thick", color: { rgb: "FFFFFF" } },
              },
            };
          } else {
            const isEvenRow = (row - headerRow) % 2 === 0;
            worksheet[`${col}${row}`].s = {
              font: { size: 11, color: { rgb: "333333" } },
              alignment: { horizontal: "left", vertical: "center" },
              fill: { fgColor: { rgb: isEvenRow ? "F8F9FA" : "FFFFFF" } },
              border: {
                top: { style: "thin", color: { rgb: "D0D0D0" } },
                bottom: { style: "thin", color: { rgb: "D0D0D0" } },
                left: { style: "thin", color: { rgb: "D0D0D0" } },
                right: { style: "thin", color: { rgb: "D0D0D0" } },
              },
            };

            if (col === "B" || col === "C" || col === "E") {
              worksheet[`${col}${row}`].s.alignment.horizontal = "center";
              worksheet[`${col}${row}`].s.font.bold = true;
            }
          }
        }
      });
    }
  }

  for (let row = range.e.r - 4; row <= range.e.r; row++) {
    if (worksheet[`A${row}`]) {
      if (worksheet[`A${row}`].v === "_________________________") {
        worksheet[`A${row}`].s = {
          font: { size: 12, color: { rgb: "333333" } },
          alignment: { horizontal: "left", vertical: "center" },
          border: {
            bottom: { style: "thin", color: { rgb: "333333" } },
          },
        };
      } else {
        worksheet[`A${row}`].s = {
          font: { size: 11, italic: true, color: { rgb: "333333" } },
          alignment: { horizontal: "left", vertical: "center" },
        };
      }
    }
  }

  return worksheet;
};
